import React, { useEffect, useState } from 'react';
import { calcularRFC, calcularCURP } from '../utils/calculoRfcCurp';

// Banorte · Obligado Solidario (mig 110). Va en BancoBanorte.jsx antes de "Información del Crédito". El valor vive en
// bn.obligado_solidario y viaja en el mismo POST /api/banorte/:id (utils/obligadoBanorte.js lo valida y guarda).
// Con "No" la sección se pliega pero lo capturado se conserva (se guarda con incluye=false y no se imprime).
// Catálogos: estado civil, régimen y tipo de domicilio de Banorte (los mismos del titular); entidad y ciudad de nacimiento y
// el domicilio por SEPOMEX, igual que en Prospectos. RFC y CURP se calculan con el botón Calcular y se pueden editar.
// Anchos (Marco 2026-10-26), en escritorio y relativos al ancho que tenían: fecha 40 %, entidad 60 %, género 33 % (subió junto a
// Ciudad de nacimiento) y Nacionalidad a su lado; estado civil 33 %, RFC y CURP 90 % y Actividad Empresarial a su lado.
// Nacionalidad y Actividad Empresarial (mig 111) se imprimen en la hoja "Solicitud Obligado Solidario".
const API = '/api';
const RFC_VALIDO = /^[A-ZÑ&]{3,4}\d{6}[A-Z\d]{3}$/;
const CURP_VALIDO = /^[A-Z][AEIOUX][A-Z]{2}\d{6}[HM][A-Z]{5}[A-Z\d]\d$/;
const CORREO_VALIDO = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ObligadoSolidarioBanorte({ valor, onChange, estadosCiviles, regimenes, tiposDomicilio, st, isMobile, isTablet }) {
  const o = valor || {};
  const [estados, setEstados] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [colonias, setColonias] = useState([]);
  const [avisoCp, setAvisoCp] = useState('');
  const set = (k, v) => onChange({ ...o, [k]: v });
  const setVarios = (cambios) => onChange({ ...o, ...cambios });
  const cols = (n) => ({ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : `repeat(${n}, 1fr)`, gap: 16, marginBottom: 4 });
  // Renglón con anchos propios: cada pista es una fracción del ancho que tenía en un renglón de n columnas.
  const anchoDe = (n, f) => `calc((100% - ${(n - 1) * 16}px) / ${n} * ${f})`;
  const colsAnchos = (pistas) => isMobile || isTablet ? cols(2) : { ...cols(1), gridTemplateColumns: pistas.join(' ') };
  const angosto = { ...st.input, paddingLeft: 6, paddingRight: 2, fontSize: 13 };

  useEffect(() => {
    if (!o.incluye || estados.length) return;
    fetch(`${API}/sepomex/estados`).then(r => r.json()).then(d => setEstados(d.map(e => e.estado || e))).catch(() => {});
  }, [o.incluye, estados.length]);
  useEffect(() => {
    if (!o.incluye || !o.entidad_nacimiento) { setMunicipios([]); return; }
    fetch(`${API}/sepomex/municipios/${encodeURIComponent(o.entidad_nacimiento)}`).then(r => r.json())
      .then(d => setMunicipios(d.map(m => m.municipio || m))).catch(() => setMunicipios([]));
  }, [o.incluye, o.entidad_nacimiento]);
  // Colonias del código postal ya guardado (al abrir el prospecto).
  useEffect(() => {
    if (!o.incluye || !/^\d{5}$/.test(o.codigo_postal || '') || colonias.length) return;
    fetch(`${API}/sepomex/cp/${o.codigo_postal}`).then(r => r.json()).then(d => setColonias(Array.isArray(d) ? d : [])).catch(() => {});
  }, [o.incluye, o.codigo_postal]); // eslint-disable-line react-hooks/exhaustive-deps

  const buscarCp = async (cp) => {
    setAvisoCp(''); setColonias([]);
    if (!/^\d{5}$/.test(cp)) return;
    try {
      const d = await (await fetch(`${API}/sepomex/cp/${cp}`)).json();
      if (Array.isArray(d) && d.length) {
        setColonias(d);
        setVarios({ codigo_postal: cp, ciudad: d[0].municipio || d[0].ciudad || '', entidad_federativa: d[0].estado || '',
          colonia: d.some(x => x.colonia === o.colonia) ? o.colonia : (d.length === 1 ? d[0].colonia : '') });
      } else setAvisoCp('Código postal no encontrado en SEPOMEX');
    } catch { setAvisoCp('No se pudo consultar el código postal'); }
  };
  const calcular = (campo) => {
    if (!o.nombres || !o.apellido_paterno || !o.fecha_nacimiento) {
      alert('Para calcular, primero captura nombre(s), apellido paterno y fecha de nacimiento del obligado solidario.');
      return;
    }
    if (campo === 'rfc') return set('rfc', calcularRFC(o));
    if (!o.sexo_genero_id || !o.entidad_nacimiento) {
      alert('Para calcular el CURP, también captura género y entidad federativa de nacimiento.');
      return;
    }
    set('curp', calcularCURP({ ...o, pais_nacimiento: 'México' }));
  };

  const esCasado = (estadosCiviles.find(e => String(e.id) === String(o.estado_civil_id)) || {}).nombre === 'Casado';
  const faltaNombre = o.incluye && !(String(o.nombres || '').trim() && String(o.apellido_paterno || '').trim());
  const avisos = [];
  if (o.incluye) {
    if (o.rfc && !RFC_VALIDO.test(o.rfc)) avisos.push('El RFC no tiene el formato esperado (12 o 13 caracteres).');
    if (o.curp && !CURP_VALIDO.test(o.curp)) avisos.push('La CURP no tiene el formato esperado (18 caracteres).');
    if (o.email && !CORREO_VALIDO.test(o.email)) avisos.push('Revisa el correo electrónico.');
    if (o.telefono_celular && String(o.telefono_celular).replace(/\D/g, '').length !== 10) avisos.push('El teléfono celular debe tener 10 dígitos.');
  }
  const campo = (k, etiqueta, props = {}) => (
    <div style={st.field}>
      <label style={st.label}>{etiqueta}</label>
      <input value={o[k] ?? ''} onChange={e => set(k, props.mayus ? e.target.value.toUpperCase().replace(/\s/g, '') : props.digitos ? e.target.value.replace(/\D/g, '') : e.target.value)}
        maxLength={props.max} type={props.type || 'text'} inputMode={props.digitos ? 'numeric' : undefined} style={st.input} />
    </div>
  );
  const lista = (k, etiqueta, opciones, { numero = true, estilo = st.input, vacio = '— Selecciona —' } = {}) => (
    <div style={st.field}>
      <label style={st.label}>{etiqueta}</label>
      <select value={o[k] ?? ''} onChange={e => set(k, e.target.value === '' ? null : numero ? Number(e.target.value) : e.target.value)} style={estilo}>
        <option value="">{vacio}</option>
        {opciones.map(x => <option key={x.id ?? x} value={x.id ?? x}>{x.nombre ?? x}</option>)}
      </select>
    </div>
  );
  const conCalcular = (k, etiqueta, max) => (
    <div style={st.field}>
      <label style={st.label}>{etiqueta}</label>
      <div style={{ display: 'flex', gap: 8 }}>
        <input value={o[k] ?? ''} maxLength={max} onChange={e => set(k, e.target.value.toUpperCase().replace(/\s/g, ''))} style={{ ...angosto, flex: 1, minWidth: 0 }} />
        <button type="button" onClick={() => calcular(k)} style={{ ...st.saveBtn, marginTop: 0, padding: '0 10px', fontSize: 13 }}>Calcular</button>
      </div>
    </div>
  );
  const aviso = { backgroundColor: '#FFF7E6', border: '1px solid #F5C26B', color: '#7A4B00', padding: '12px 16px', borderRadius: 8, fontSize: 13, marginBottom: 16 };
  const sub = { fontSize: 15, fontWeight: '600', color: '#374151', margin: '16px 0 8px' };

  return (
    <div style={st.card}>
      <h3 style={st.title}>🤝 Obligado Solidario</h3>
      <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap', marginBottom: o.incluye ? 16 : 0 }}>
        <span style={{ fontSize: 14, fontWeight: '600', color: '#374151' }}>¿Se incluye un Obligado Solidario?</span>
        {[[true, 'Sí'], [false, 'No']].map(([v, t]) => (
          <label key={t} style={{ fontSize: 14, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input type="radio" name="banorte-obligado-incluye" checked={(o.incluye === true) === v} onChange={() => set('incluye', v)} /> {t}
          </label>
        ))}
      </div>
      {!o.incluye && o.nombres && (
        <p style={{ fontSize: 13, color: '#6b7280', margin: '12px 0 0' }}>Los datos capturados del obligado se conservan; no se imprimen mientras la respuesta sea No.</p>
      )}
      {o.incluye && (
        <>
          {faltaNombre && <div style={aviso}>Captura al menos nombre(s) y apellido paterno: mientras falten, el obligado solidario no se imprime.</div>}
          {avisos.length > 0 && <div style={aviso}>{avisos.map(a => <div key={a}>{a}</div>)}</div>}
          <div style={cols(3)}>
            {campo('nombres', 'Nombre(s)', { max: 100 })}
            {campo('apellido_paterno', 'Apellido Paterno', { max: 100 })}
            {campo('apellido_materno', 'Apellido Materno', { max: 100 })}
          </div>
          <div style={colsAnchos([anchoDe(3, 0.4), anchoDe(3, 0.6), anchoDe(3, 1), anchoDe(4, 0.33), '180px'])}>
            {campo('fecha_nacimiento', 'Fecha de nacimiento', { type: 'date' })}
            <div style={st.field}>
              <label style={st.label}>Entidad Fed. de nacimiento</label>
              <select value={o.entidad_nacimiento || ''} onChange={e => setVarios({ entidad_nacimiento: e.target.value || null, municipio_nacimiento: null })} style={st.input}>
                <option value="">— Selecciona —</option>
                {estados.map(x => <option key={x} value={x}>{x}</option>)}
              </select>
            </div>
            {lista('municipio_nacimiento', 'Ciudad de nacimiento', municipios, { numero: false })}
            {lista('sexo_genero_id', 'Género', [{ id: 1, nombre: 'Masculino' }, { id: 2, nombre: 'Femenino' }], { estilo: angosto, vacio: '—' })}
            {campo('nacionalidad', 'Nacionalidad', { max: 50 })}
          </div>
          <div style={colsAnchos([anchoDe(4, 0.33), ...(esCasado ? [anchoDe(4, 1)] : []), anchoDe(4, 0.9), anchoDe(4, 0.9), '180px'])}>
            {lista('estado_civil_id', 'Estado civil', estadosCiviles, { estilo: angosto, vacio: '—' })}
            {esCasado && lista('regimen_conyugal_id', 'Régimen matrimonial', regimenes)}
            {conCalcular('rfc', 'RFC', 13)}
            {conCalcular('curp', 'CURP', 18)}
            <div style={st.field}>
              <label style={st.label}>Actividad Empresarial</label>
              <select value={o.actividad_empresarial === true ? 'true' : o.actividad_empresarial === false ? 'false' : ''}
                onChange={e => set('actividad_empresarial', e.target.value === '' ? null : e.target.value === 'true')} style={st.input}>
                <option value="">— Selecciona —</option>
                <option value="true">Sí</option>
                <option value="false">No</option>
              </select>
            </div>
          </div>
          <div style={sub}>Domicilio</div>
          <div style={cols(4)}>
            {campo('calle', 'Calle', { max: 200 })}
            {campo('no_ext', 'No. Ext.', { max: 50 })}
            {campo('no_int', 'No. Int.', { max: 50 })}
            <div style={st.field}>
              <label style={st.label}>Código Postal</label>
              <input value={o.codigo_postal || ''} maxLength={5} inputMode="numeric"
                onChange={e => { const cp = e.target.value.replace(/\D/g, ''); set('codigo_postal', cp); if (cp.length === 5) buscarCp(cp); }} style={st.input} />
              {avisoCp && <div style={{ fontSize: 12, color: '#b45309', marginTop: 4 }}>{avisoCp}</div>}
            </div>
          </div>
          <div style={cols(3)}>
            <div style={st.field}>
              <label style={st.label}>Colonia</label>
              <select value={o.colonia || ''} onChange={e => set('colonia', e.target.value || null)} style={st.input} disabled={!colonias.length}>
                <option value="">{colonias.length ? '— Selecciona —' : 'Captura el código postal'}</option>
                {[...new Set(colonias.map(x => x.colonia))].map(x => <option key={x} value={x}>{x}</option>)}
                {o.colonia && !colonias.some(x => x.colonia === o.colonia) && <option value={o.colonia}>{o.colonia}</option>}
              </select>
            </div>
            <div style={st.field}><label style={st.label}>Ciudad</label><input readOnly value={o.ciudad || ''} style={{ ...st.input, ...st.readOnly }} /></div>
            <div style={st.field}><label style={st.label}>Entidad Federativa</label><input readOnly value={o.entidad_federativa || ''} style={{ ...st.input, ...st.readOnly }} /></div>
          </div>
          <div style={cols(4)}>
            {campo('telefono_casa', 'Tel. Casa', { max: 10, digitos: true })}
            {campo('telefono_celular', 'Tel. Celular', { max: 10, digitos: true })}
            {campo('nivel_academico_profesion', 'Nivel Académico / Profesión', { max: 150 })}
          </div>
          <div style={cols(4)}>
            {lista('tipo_domicilio_id', 'Tipo de Domicilio', tiposDomicilio)}
            {campo('email', 'e-mail', { max: 100, type: 'email' })}
            {campo('antiguedad_domicilio_anos', 'Antigüedad actual (años)', { max: 2, digitos: true })}
            {campo('antiguedad_domicilio_meses', 'Antigüedad actual (meses)', { max: 2, digitos: true })}
          </div>
        </>
      )}
    </div>
  );
}
